import { AfterViewInit, Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { debounceTime, merge, Subject } from 'rxjs';
import { MatDialog } from '@angular/material/dialog';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort, SortDirection } from '@angular/material/sort';
import { AccountService } from '../account.service';
import { AccountDataSource } from '../account.data-source';
import { RemoveAccountDialogComponent } from '../remove-account-dialog/remove-account-dialog.component';
import { NotificationService } from '../../shared/service/notification.service';

@Component({
  selector: 'app-account-overview',
  templateUrl: './account-overview.component.html',
  styleUrls: ['./account-overview.component.scss']
})
export class AccountOverviewComponent implements AfterViewInit, OnDestroy, OnInit {

  private searchInputSubject = new Subject<string>();
  public searchInput = this.searchInputSubject.asObservable();
  public searchText = '';
  private searchEmpty = true;
  public tableColumns: string[] = [
    'email',
    'firstName',
    'lastName',
    'language',
    'roles',
    'isB2bUser',
    'createdAt',
    'lastActivity',
    'actions'
  ];

  public sortActiveDefault = 'email';
  public sortDirectionDefault: SortDirection = 'asc';
  public pageSizeOptions: number[] = [15, 30, 50];
  public totalItems = 0;
  public dataSource: AccountDataSource;

  @ViewChild(MatPaginator) paginator!: MatPaginator;
  @ViewChild(MatSort) sort!: MatSort;

  constructor(
    private accountService: AccountService,
    private dialog: MatDialog,
    private notificationService: NotificationService
  ) {
    this.dataSource = new AccountDataSource(this.accountService);

    this.dataSource.dataLength.subscribe((total) => {
      this.totalItems = total
    });
  }

  ngOnInit() {
    this.loadData();
  }

  ngAfterViewInit() {
    // Reset pagination after sort change.
    this.sort.sortChange
      .subscribe(() => this.paginator.pageIndex = 0);

    // Load data when a sort or paginator event is emitted.
    merge(this.sort.sortChange, this.paginator.page)
      .subscribe(() => this.loadData());

    // Load data when search input value changes.
    this.searchInput
      .pipe(
        debounceTime(150)
      )
      .subscribe((value) => {
        if (!this.searchEmpty || value.length) {
          this.paginator.pageIndex = 0;
          this.loadData()
        }

        this.searchEmpty = value.length === 0;
      });
  }

  ngOnDestroy() {
    this.searchInputSubject.unsubscribe();
  }

  public onSearch(): void {
    this.searchInputSubject.next(this.searchText);
  }

  public onSearchClear(): void {
    this.searchText = '';
    this.onSearch();
  }

  public onRemoveAction(id: string, email: string): void {
    if (this.dialog.openDialogs.length) {
      return;
    }

    this.dialog
      .open(RemoveAccountDialogComponent, {
        data: {
          id: id,
          email: email,
        },
      })
      .afterClosed()
      .subscribe(
      data => {
        if (data?.id) {
          this.deleteAccount(data.id);
        }
      }
    );
  }

  private deleteAccount(accountId: string): void {
    this.accountService
      .delete(accountId)
      .subscribe({
        next: () => {
          this.notificationService.success_ts('account.deleted');
          this.loadData();
        }
      })
  }

  private loadData() {
    this.dataSource.loadAccounts(
      this.paginator?.pageIndex + 1 || 1,
      this.paginator?.pageSize || this.pageSizeOptions[0],
      this.sort?.active || this.sortActiveDefault,
      this.sort?.direction || this.sortDirectionDefault,
      this.searchText
    );
  }
}
